import { FunnelIcon } from "@heroicons/react/24/outline";
import { CustomListItemId } from "@sp-crm/core";
import { SearchbarItemBadge } from "components/community-search/search-bar/searchbar-item-badge";
import { StatelessSearchbarItem } from "components/community-search/search-bar/stateless-searchbar-item";
import { Checkbox } from "components/ui/checkbox";
import { Radio } from "components/ui/radio";
import { SecondaryButton } from "components/ui/secondary-button";
import React from "react";
import {
    availableClientStatusGroupOptions,
    ClientStatusGroup,
    clientStatusGroupOptions,
    groupForStatusIds,
    HomeCareStatusList,
    statusIdsForGroup,
} from "./status-groups";

interface ClientStatusFilterProps {
    customList: HomeCareStatusList;
    value: CustomListItemId[];
    onChange: (statusIds: CustomListItemId[]) => void;
}

const presetSections: { heading: string; groups: ClientStatusGroup[] }[] = [
    { heading: "Open", groups: ["leads", "activeClients"] },
    { heading: "Closed", groups: ["closedLost", "previousClients"] },
];

const optionForGroup = (group: ClientStatusGroup) =>
    clientStatusGroupOptions.find(option => option.value === group);

const triggerLabel = (group: ClientStatusGroup | null): string => {
    if (group === null || group === "all") {
        return "Status";
    }
    const option = clientStatusGroupOptions.find(o => o.value === group);
    return option ? `Status: ${option.label}` : "Status";
};

export const ClientStatusFilter: React.FC<ClientStatusFilterProps> = props => {
    const { customList, value, onChange } = props;

    const selectedGroup = groupForStatusIds(customList, value);
    const availableGroups = availableClientStatusGroupOptions(customList).map(
        option => option.value,
    );

    const selectPreset = (group: ClientStatusGroup) => {
        if (group === "all") {
            onChange([]);
            return;
        }
        onChange(statusIdsForGroup(customList, group));
    };

    const check = (id: CustomListItemId) => {
        if (!value.includes(id)) {
            onChange([...value, id]);
        }
    };

    const uncheck = (id: CustomListItemId) => {
        onChange(value.filter(v => v !== id));
    };

    const items = customList?.items ?? [];
    const statusSections = (customList?.categories ?? [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map(category => ({
            category,
            items: items.filter(item => item.customListCategoryId === category.id),
        }))
        .filter(section => section.items.length > 0);

    return (
        <StatelessSearchbarItem
            content={
                <div className="flex items-center space-x-2">
                    <FunnelIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">{triggerLabel(selectedGroup)}</span>
                    {selectedGroup === null && value.length > 0 ? (
                        <SearchbarItemBadge counter={value.length} />
                    ) : null}
                </div>
            }>
            <div className="w-64 p-1 space-y-3">
                <div className="space-y-2">
                    <Radio
                        name="client-status-group"
                        checked={selectedGroup === "all"}
                        onChange={() => selectPreset("all")}
                        label="All statuses"
                    />
                    <div className="border-t border-gray-200 pt-2 space-y-2">
                        {presetSections
                            .map(section => ({
                                heading: section.heading,
                                groups: section.groups.filter(group =>
                                    availableGroups.includes(group),
                                ),
                            }))
                            .filter(section => section.groups.length > 0)
                            .map(section => (
                                <React.Fragment key={section.heading}>
                                    <p className="text-xs font-medium uppercase text-gray-500">
                                        {section.heading}
                                    </p>
                                    {section.groups.map(group => (
                                        <Radio
                                            key={group}
                                            name="client-status-group"
                                            checked={selectedGroup === group}
                                            onChange={() => selectPreset(group)}
                                            label={optionForGroup(group)?.label ?? group}
                                        />
                                    ))}
                                </React.Fragment>
                            ))}
                    </div>
                </div>
                <div className="border-t border-gray-200 pt-3 space-y-2">
                    {statusSections.map(section => (
                        <React.Fragment key={section.category.id}>
                            <p className="text-xs font-medium uppercase text-gray-500">
                                {section.category.name}
                            </p>
                            {section.items.map(item => (
                                <Checkbox
                                    key={item.id}
                                    checked={value.includes(item.id)}
                                    onChange={e =>
                                        e.target.checked
                                            ? check(item.id)
                                            : uncheck(item.id)
                                    }
                                    label={item.name}
                                />
                            ))}
                        </React.Fragment>
                    ))}
                </div>
                {value.length > 0 ? (
                    <div className="flex items-center justify-end">
                        <SecondaryButton type="button" onClick={() => onChange([])}>
                            Clear
                        </SecondaryButton>
                    </div>
                ) : null}
            </div>
        </StatelessSearchbarItem>
    );
};
